import { ReactNode, useEffect, useState } from "react";
import type { OrdenCompraGroupedResponse } from "./index";
import "./conversionMoneda.css";

/* ------------------------------------------------------------------ */
/* Exchange rates (mindicador.cl, the source the app already uses)      */
/* ------------------------------------------------------------------ */

// Order currencies that need conversion (ocTipmonId). CLP (1) and
// "Peso CL" (39) are already in pesos
const MONEDAS: Record<
  number,
  { indicador: string; codigo: string; simbolo: string; decimales: number }
> = {
  2: { indicador: "dolar", codigo: "USD", simbolo: "US$", decimales: 2 },
  3: { indicador: "euro", codigo: "EUR", simbolo: "€", decimales: 2 },
  40: { indicador: "uf", codigo: "UF", simbolo: "UF", decimales: 4 },
};

interface Observacion {
  fecha: string; // YYYY-MM-DD
  valor: number;
}

// One request per currency and year (~10-20 KB), shared by every row
const seriesCache = new Map<string, Promise<Observacion[]>>();

const serieAnual = (indicador: string, anio: number) => {
  const key = `${indicador}-${anio}`;
  if (!seriesCache.has(key)) {
    const promesa = fetch(`https://mindicador.cl/api/${indicador}/${anio}`)
      .then((r) => {
        if (!r.ok) throw new Error(`mindicador ${r.status}`);
        return r.json();
      })
      .then((j: { serie?: { fecha: string; valor: number }[] }) =>
        (j.serie ?? [])
          .map((o) => ({ fecha: o.fecha.slice(0, 10), valor: Number(o.valor) }))
          .sort((a, b) => (a.fecha < b.fecha ? 1 : -1)),
      )
      .catch((e) => {
        seriesCache.delete(key); // allow a retry on the next render
        throw e;
      });
    seriesCache.set(key, promesa);
  }
  return seriesCache.get(key)!;
};

/**
 * Value on a date, or the last one published before it (dólar and euro have
 * no value on weekends / holidays). Looks into the previous year for early
 * January dates.
 */
const valorEnFecha = async (
  indicador: string,
  fecha: string,
): Promise<Observacion | null> => {
  const anio = Number(fecha.slice(0, 4));
  const enAnio = (await serieAnual(indicador, anio)).find(
    (o) => o.fecha <= fecha,
  );
  if (enAnio) return enAnio;
  return (await serieAnual(indicador, anio - 1))[0] ?? null;
};

// Last calendar day of the month of a YYYY-MM-DD date
const finDeMes = (fecha: string) => {
  const [y, m] = fecha.split("-").map(Number);
  const d = new Date(y, m, 0);
  return `${y}-${String(m).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/* ------------------------------------------------------------------ */
/* Order amounts                                                        */
/* ------------------------------------------------------------------ */

/**
 * Amount of the order in its own currency: the sum of its lines
 * (precioTotal is always in the order currency). The header total is not
 * reliable for foreign currencies: it can miss lines added later (e.g. OC
 * 71376: header 124,89 UF vs lines 165,42 UF) and rounds UF to whole units.
 * Fallback without lines: with ocTasaCambio > 1 montoTotalNetoOrden is in
 * pesos at that rate, with ocTasaCambio = 1 it is in the original currency.
 */
const montoOriginal = (o: OrdenCompraGroupedResponse) => {
  const lineas = (o.detallesOrden ?? []).reduce(
    (acc, d) => acc + (Number(d.precioTotal) || 0),
    0,
  );
  if (lineas > 0) return lineas;
  const monto = Number(o.montoTotalNetoOrden) || 0;
  const tasa = Number(o.ocTasaCambio) || 1;
  return tasa > 1 ? monto / tasa : monto;
};

// Latest invoice date (YYYY-MM-DD), the same reference the "cumplida" flow
// uses for the reception
const ultimaFechaFactura = (o: OrdenCompraGroupedResponse) =>
  (o.facturas ?? [])
    .map((f) => (f.fechaFactura ?? "").slice(0, 10))
    .filter((f) => /^\d{4}-\d{2}-\d{2}$/.test(f))
    .sort()
    .pop() ?? null;

// Same tolerance as the existing "cumplida" rule
// Document types (tipoDocumento): 70 Factura afecta, 71 Nota de credito,
// 77 Factura exenta
export const TIPO_FACTURA_EXENTA = 77;

/**
 * Net amount of an invoice from its totals detail (FcoTotNom / FcoTotVal).
 *  - 77 Factura exenta: it has no "Valor Neto" line (and montoFactura is
 *    usually 0), so its net is "Valor Exento" (+ "Valor Neto" if any).
 *  - Any other type (70, 71...): "Valor Neto", else montoFactura, as before.
 */
export const netoFactura = (
  detalles: { FcoTotNom?: string; FcoTotVal?: number | string }[],
  montoFactura?: number | string | null,
  tipoDocumento?: number | string | null,
): number => {
  const nombre = (det: { FcoTotNom?: string }) => det?.FcoTotNom?.trimEnd();
  const lineas = detalles ?? [];
  let neto: number;
  if (Number(tipoDocumento) === TIPO_FACTURA_EXENTA) {
    const partes = lineas.filter(
      (det) => nombre(det) === "Valor Neto" || nombre(det) === "Valor Exento",
    );
    neto = partes.length
      ? partes.reduce((s, det) => s + (Number(det.FcoTotVal) || 0), 0)
      : Number(montoFactura) || 0;
  } else {
    const netoObj = lineas.find((det) => nombre(det) === "Valor Neto");
    neto = netoObj ? Number(netoObj.FcoTotVal) : Number(montoFactura) || 0;
  }
  return Number.isFinite(neto) ? neto : 0;
};

export const dentroDe10Porciento = (montoOc: number, sumNeto: number) => {
  const diff = Math.abs(montoOc - sumNeto);
  return montoOc > 0 ? diff / montoOc <= 0.1 : diff === 0;
};

interface Conversion {
  fechaTasa: string;
  tasa: number;
  montoClp: number;
  diferenciaPct: number;
  cumple: boolean;
}

interface EstadoConversion {
  aplica: boolean; // foreign currency order with an invoice date
  loading: boolean;
  error: boolean;
  dia: Conversion | null; // rate of the invoice date
  finMes: Conversion | null; // rate of the last day of the invoice month
}

const useConversionOrden = (
  orden: OrdenCompraGroupedResponse,
  sumNetoFacturas: number,
): EstadoConversion => {
  const moneda = MONEDAS[Number(orden.ocTipmonId)];
  const fecha = ultimaFechaFactura(orden);
  const original = montoOriginal(orden);
  const aplica = !!moneda && !!fecha;
  const [estado, setEstado] = useState<EstadoConversion>({
    aplica,
    loading: aplica,
    error: false,
    dia: null,
    finMes: null,
  });

  useEffect(() => {
    if (!aplica || !moneda || !fecha) {
      setEstado({
        aplica: false,
        loading: false,
        error: false,
        dia: null,
        finMes: null,
      });
      return;
    }
    let vivo = true;
    const convertir = (obs: Observacion | null): Conversion | null => {
      if (!obs) return null;
      const montoClp = original * obs.valor;
      return {
        fechaTasa: obs.fecha,
        tasa: obs.valor,
        montoClp,
        diferenciaPct: montoClp
          ? (Math.abs(montoClp - sumNetoFacturas) / montoClp) * 100
          : 0,
        cumple: dentroDe10Porciento(montoClp, sumNetoFacturas),
      };
    };
    setEstado((e) => ({ ...e, aplica: true, loading: true, error: false }));
    Promise.all([
      valorEnFecha(moneda.indicador, fecha),
      valorEnFecha(moneda.indicador, finDeMes(fecha)),
    ])
      .then(([dia, fin]) => {
        if (!vivo) return;
        setEstado({
          aplica: true,
          loading: false,
          error: false,
          dia: convertir(dia),
          finMes: convertir(fin),
        });
      })
      .catch((e) => {
        console.error("Error obteniendo tipo de cambio:", e);
        if (!vivo) return;
        setEstado({
          aplica: true,
          loading: false,
          error: true,
          dia: null,
          finMes: null,
        });
      });
    return () => {
      vivo = false;
    };
  }, [aplica, moneda, fecha, original, sumNetoFacturas]);

  return estado;
};

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

const clp = (v: number) =>
  `$ ${Math.round(v).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
const fechaCl = (iso: string) => iso.split("-").reverse().join("-");

interface AccionesCumplimientoProps {
  orden: OrdenCompraGroupedResponse;
  sumNetoFacturas: number;
  hasPaidInvoice: boolean;
  // Existing rule: montoTotalNetoOrden vs invoices within 10%
  cumpleMontoOc: boolean;
  // Buttons, rendered by the table with its existing markup and handlers
  renderCumplir: () => ReactNode;
  renderSemiCumplir: () => ReactNode;
}

/**
 * Informative conversion of a foreign-currency OC (USD / EUR / UF) to pesos
 * at the invoice-date rate and at the month-end rate, plus the "cumplida" /
 * "semi cumplida" buttons. "Cumplida" is offered when the existing rule OR
 * either converted amount is within 10% of the invoices.
 */
export default function AccionesCumplimiento({
  orden,
  sumNetoFacturas,
  hasPaidInvoice,
  cumpleMontoOc,
  renderCumplir,
  renderSemiCumplir,
}: AccionesCumplimientoProps) {
  const conv = useConversionOrden(orden, sumNetoFacturas);
  const moneda = MONEDAS[Number(orden.ocTipmonId)];

  const cumple = cumpleMontoOc || !!conv.dia?.cumple || !!conv.finMes?.cumple;
  // Wait for the rates so the buttons don't switch after loading
  const decidido = !conv.aplica || !conv.loading;
  const showCumplirBtn =
    decidido && hasPaidInvoice && cumple && orden.estadoOrden !== 4;
  const showSemiCumplirBtn =
    decidido && !showCumplirBtn && hasPaidInvoice && orden.estadoOrden === 2;

  const pctTexto = (v: number) =>
    v.toLocaleString("es-CL", { maximumFractionDigits: 1 });

  // One comparison line: label, OC amount in pesos, difference vs invoices
  const linea = (
    key: string,
    titulo: ReactNode,
    montoClp: number,
    cumpleLinea: boolean,
  ) => {
    const diferenciaPct = montoClp
      ? (Math.abs(montoClp - sumNetoFacturas) / montoClp) * 100
      : 0;
    return (
      <div className="fm-conv-linea" key={key}>
        <span>{titulo}:</span>
        <strong>{clp(montoClp)}</strong>
        <span className={cumpleLinea ? "fm-conv-ok" : "fm-conv-dif"}>
          {cumpleLinea ? <i className="pi pi-check-circle" /> : null} dif.{" "}
          {pctTexto(diferenciaPct)}%
        </span>
      </div>
    );
  };

  const lineaTasa = (titulo: string, c: Conversion | null) =>
    c
      ? linea(
          titulo,
          <>
            {titulo} {fechaCl(c.fechaTasa)} ({moneda?.codigo} ${" "}
            {c.tasa.toLocaleString("es-CL", { maximumFractionDigits: 2 })})
          </>,
          c.montoClp,
          c.cumple,
        )
      : null;

  const monto = Number(orden.montoTotalNetoOrden) || 0;
  const tasaOc = Number(orden.ocTasaCambio) || 1;
  const tieneFacturas = (orden.facturas ?? []).length > 0;

  return (
    <div className="fm-acciones">
      {showCumplirBtn ? renderCumplir() : null}
      {showSemiCumplirBtn ? renderSemiCumplir() : null}

      {tieneFacturas ? (
        <div
          className="fm-conv"
          title="Informativo: monto de la OC comparado con el neto de sus facturas"
        >
          <div className="fm-conv-titulo">
            <i className="pi pi-money-bill" /> Cuadratura OC vs facturas
            {moneda ? (
              <>
                {" "}
                · OC en {moneda.codigo}:{" "}
                <strong>
                  {moneda.simbolo}{" "}
                  {montoOriginal(orden).toLocaleString("es-CL", {
                    maximumFractionDigits: moneda.decimales,
                  })}
                </strong>
              </>
            ) : null}
            <span className="fm-conv-sub">
              · facturas netas {clp(sumNetoFacturas)}
            </span>
          </div>

          {/* Pesos orders, or foreign orders priced in pesos at the OC rate */}
          {!moneda
            ? linea("oc", "Monto OC", monto, cumpleMontoOc)
            : tasaOc > 1
              ? linea(
                  "oc",
                  `Al cambio de la OC (${moneda.codigo} $ ${tasaOc.toLocaleString(
                    "es-CL",
                    { maximumFractionDigits: 2 },
                  )})`,
                  monto,
                  cumpleMontoOc,
                )
              : null}

          {moneda ? (
            conv.loading ? (
              <div className="fm-conv-linea">Calculando tipo de cambio…</div>
            ) : conv.error ? (
              <div className="fm-conv-linea fm-conv-dif">
                No se pudo obtener el tipo de cambio (mindicador.cl)
              </div>
            ) : (
              <>
                {lineaTasa("Cambio fecha factura", conv.dia)}
                {lineaTasa("Cambio fin de mes", conv.finMes)}
              </>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
